/**
 * The Help index: every guide, plus the existing Providers page, which keeps
 * its own address rather than moving under `/help`. Adding a guide means
 * adding it to `../help/guides.ts` — this page reads that list rather than
 * naming guides itself.
 */

import { Link } from "react-router-dom";

import { GUIDES } from "../help/guides";

/** One card's worth of Help index entry: a place to go and what's there.
 * Deliberately not a `Guide` — Providers isn't one, since it keeps its
 * pre-existing address rather than moving under `/help/` — but it reads as
 * one on this page, so it's folded into the same list rather than hand-
 * written as a second, differently-shaped `<li>`. Addresses are unique, so
 * `to` is also the list key; nothing here restates a guide's slug. */
interface IndexEntry {
  to: string;
  title: string;
  summary: string;
}

const PROVIDERS_ENTRY: IndexEntry = {
  to: "/providers",
  title: "Cloud providers, threads and pack size",
  summary:
    "What each kind of storage costs, why the default thread counts differ between them, and how to tune both on purpose instead of by guessing.",
};

const ENTRIES: IndexEntry[] = [
  ...GUIDES.map(({ slug, title, summary }) => ({ to: `/help/${slug}`, title, summary })),
  PROVIDERS_ENTRY,
];

export function Help() {
  return (
    <>
      <section className="page-header">
        <h1>Help</h1>
        <p className="muted">
          Step-by-step guides for the parts of a backup that happen outside this app — setting up
          a cloud account, and the choices its console asks you to make along the way.
        </p>
      </section>

      <ul className="help-index">
        {ENTRIES.map((entry) => (
          <li key={entry.to} className="panel help-index__entry">
            <h2>
              <Link to={entry.to}>{entry.title}</Link>
            </h2>
            <p className="muted">{entry.summary}</p>
          </li>
        ))}
      </ul>
    </>
  );
}
